import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import AdsBanner from '@/components/feature/AdsBanner';
import { supabase } from '@/lib/supabase';
import {
  mockForumTopics,
  forumCategories,
  type ForumTopic,
} from '@/mocks/forum';
import {
  mapDbForumTopic,
  formatViews,
  timeAgo,
  type ForumTopicDbRow,
} from '@/lib/forum';

const forumHero =
  'https://readdy.ai/api/search-image?query=Modern%20African%20community%20discussion%20platform%2C%20diverse%20people%20collaborating%20and%20sharing%20ideas%20around%20a%20table%2C%20warm%20welcoming%20atmosphere%2C%20digital%20community%20concept%2C%20clean%20minimal%20background%2C%20professional%20editorial%20photography&width=1600&height=800&seq=forum-hero-dyn-01&orientation=landscape';

type SortKey = 'recent' | 'views' | 'replies';

function ForumPage() {
  const [topics, setTopics] = useState<ForumTopic[]>(mockForumTopics);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string | null>(null);
  const [sort, setSort] = useState<SortKey>('recent');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: err } = await supabase
        .from('forum_topics')
        .select('*')
        .eq('status', 'active')
        .order('created_at', { ascending: false });
      if (err) throw err;

      const rows = (data as ForumTopicDbRow[]) || [];
      if (rows.length === 0) return;

      // Auteurs
      const ownerIds = Array.from(new Set(rows.map((r) => r.owner_id)));
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', ownerIds);
      const nameMap: Record<string, string> = {};
      (profiles || []).forEach((p) => {
        nameMap[p.id] = p.full_name;
      });

      // Comptage des réponses
      const topicIds = rows.map((r) => r.id);
      const { data: replies } = await supabase
        .from('forum_replies')
        .select('topic_id')
        .in('topic_id', topicIds);
      const countMap: Record<string, number> = {};
      (replies || []).forEach((r) => {
        countMap[r.topic_id] = (countMap[r.topic_id] || 0) + 1;
      });

      setTopics(
        rows.map((row) =>
          mapDbForumTopic(row, nameMap[row.owner_id] || '', countMap[row.id] || 0),
        ),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger les discussions.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = topics.filter((topic) => {
    const q = search.toLowerCase();
    const matchesSearch =
      !q ||
      topic.title.toLowerCase().includes(q) ||
      topic.body.toLowerCase().includes(q) ||
      topic.authorName.toLowerCase().includes(q);
    const matchesCategory = !category || topic.category === category;
    return matchesSearch && matchesCategory;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sort === 'views') return b.views - a.views;
    if (sort === 'replies') return b.repliesCount - a.repliesCount;
    return b.createdAt.localeCompare(a.createdAt);
  });

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Navbar />

      <main className="flex-1">
        {/* Hero */}
        <section className="relative min-h-[420px] md:min-h-[500px] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={forumHero}
              alt="WorldApeiron Forum - communauté et discussions"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/90 to-background-50/50" />
          </div>

          <div className="relative z-10 w-full px-4 md:px-6 py-16">
            <div className="max-w-3xl">
              <span className="inline-block text-sm font-semibold text-secondary-400 uppercase tracking-wider mb-4">
                WorldApeiron Forum
              </span>
              <h1 className="font-heading font-bold text-3xl md:text-5xl text-foreground-50 mb-4 leading-tight">
                La communauté de l&apos;écosystème
              </h1>
              <p className="text-base md:text-lg text-foreground-400 max-w-xl mb-8 leading-relaxed">
                Posez vos questions, partagez vos expériences et collaborez avec les membres
                de la communauté. Construisez votre réputation au fil de vos échanges.
              </p>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <div className="flex-1 max-w-xl flex items-center bg-background-50 border border-background-300/60 rounded-full overflow-hidden focus-within:border-secondary-400">
                  <div className="w-11 h-11 flex items-center justify-center shrink-0">
                    <i className="ri-search-line text-foreground-400" />
                  </div>
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Rechercher une discussion..."
                    className="flex-1 bg-transparent text-sm text-foreground-50 placeholder:text-foreground-500 py-3 pr-4 outline-none"
                  />
                </div>
                <Link
                  to="/dashboard/forum/new"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-secondary-500 text-foreground-950 font-semibold rounded-full hover:bg-secondary-400 transition-colors text-sm whitespace-nowrap"
                >
                  <i className="ri-add-line text-lg" />
                  Nouvelle discussion
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Pub */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 pt-6">
          <AdsBanner pageKey="forum" />
        </section>

        {/* Filters */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <div className="flex items-center justify-between gap-4 flex-wrap mb-3">
            <span className="text-sm font-semibold text-foreground-400">
              {filtered.length} discussion{filtered.length > 1 ? 's' : ''}
            </span>
            <div className="flex items-center gap-2">
              <label htmlFor="forum-sort" className="text-sm text-foreground-400">Trier par</label>
              <select
                id="forum-sort"
                value={sort}
                onChange={(e) => setSort(e.target.value as SortKey)}
                className="bg-background-100 border border-background-300/60 rounded-md px-3 py-2 text-sm text-foreground-50 outline-none focus:border-secondary-400"
              >
                <option value="recent">Plus récentes</option>
                <option value="views">Plus vues</option>
                <option value="replies">Plus de réponses</option>
              </select>
            </div>
          </div>

          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setCategory(null)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                category === null
                  ? 'bg-secondary-500 text-foreground-950'
                  : 'bg-background-100 text-foreground-500 border border-background-200/70 hover:border-secondary-400/40'
              }`}
            >
              Toutes les catégories
            </button>
            {forumCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  category === cat
                    ? 'bg-secondary-500 text-foreground-950'
                    : 'bg-background-100 text-foreground-500 border border-background-200/70 hover:border-secondary-400/40'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </section>

        {/* Topics list */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 pb-16">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-secondary-500/10">
                <i className="ri-loader-4-line text-2xl text-secondary-400 animate-spin" />
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <p className="text-foreground-400 mb-4">{error}</p>
              <button
                onClick={load}
                className="px-6 py-2.5 bg-secondary-500 text-foreground-950 font-semibold rounded-lg hover:bg-secondary-400 transition-colors text-sm whitespace-nowrap"
              >
                Réessayer
              </button>
            </div>
          ) : sorted.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-secondary-500/10">
                <i className="ri-chat-3-line text-3xl text-secondary-400" />
              </div>
              <h2 className="font-heading font-bold text-xl text-foreground-50 mb-2">
                Aucune discussion trouvée
              </h2>
              <p className="text-sm text-foreground-400 mb-6">
                Aucune discussion ne correspond à votre recherche. Lancez la première !
              </p>
              <Link
                to="/dashboard/forum/new"
                className="inline-block px-6 py-2.5 bg-secondary-500 text-foreground-950 font-semibold rounded-lg hover:bg-secondary-400 transition-colors text-sm"
              >
                Créer une discussion
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {sorted.map((topic) => (
                <Link
                  key={topic.id}
                  to={`/forum/${topic.id}`}
                  className="block rounded-xl bg-background-100 border border-background-200/70 p-5 hover:border-secondary-400/40 transition-all duration-300"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-full bg-secondary-500/10 flex items-center justify-center shrink-0">
                      <i className="ri-discuss-line text-xl text-secondary-400" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1.5">
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-secondary-500/10 text-secondary-400 whitespace-nowrap">
                          {topic.category}
                        </span>
                        <span className="text-xs text-foreground-400">
                          par {topic.authorName} · {timeAgo(topic.createdAt)}
                        </span>
                      </div>
                      <h2 className="font-heading font-semibold text-foreground-50 leading-snug hover:text-secondary-400 transition-colors">
                        {topic.title}
                      </h2>
                      <p className="text-sm text-foreground-400 mt-1.5 line-clamp-2 leading-relaxed">
                        {topic.body}
                      </p>
                    </div>

                    <div className="flex items-center gap-4 shrink-0 text-xs text-foreground-400">
                      <span className="flex items-center gap-1.5 whitespace-nowrap">
                        <i className="ri-eye-line" />
                        {formatViews(topic.views)}
                      </span>
                      <span className="flex items-center gap-1.5 whitespace-nowrap">
                        <i className="ri-chat-3-line" />
                        {topic.repliesCount} réponse{topic.repliesCount > 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* CTA */}
        <section className="py-16 md:py-20 bg-background-100 border-t border-background-200/70">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-50 mb-3">
              Vous avez une question ?
            </h2>
            <p className="text-foreground-400 mb-8">
              Rejoignez la discussion et profitez de l&apos;expérience de toute la communauté.
            </p>
            <Link
              to="/dashboard/forum/new"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-secondary-500 text-foreground-950 font-semibold rounded-lg hover:bg-secondary-400 transition-colors whitespace-nowrap"
            >
              <i className="ri-add-circle-line text-lg" />
              Lancer une discussion
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default ForumPage;